import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { MakeReadyContext } from './MakeReady'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined'
import getSectionStatusSummary from './getStatusSummary'
import RequiredItemRow from './RequiredItemRow'
import ObsoleteRow from './ObsoleteRow'
import PickList from './PickList'
import { TenantSettingsContext, UserContext } from '../../../App'
import { downloadPdf } from '../../../helpers/downloadPdf'
import API from '../../../services/API'
import moment from 'moment'

const safeArray = (v) => (Array.isArray(v) ? v : [])

export const norm = (v) =>
  String(v || '')
    .trim()
    .toLowerCase()

export const parseDate = (value) => {
  if (!value) return null
  if (value instanceof Date) return value
  if (moment.isMoment(value)) return value.toDate()
  if (typeof value === 'string') return new Date(value)
  if (value.$date) return new Date(value.$date)
  return null
}

export const parseExpiryDate = (value) => {
  if (!value) return null

  if (moment.isMoment(value)) {
    return value.isValid() ? value.clone().startOf('day').toDate() : null
  }

  if (value instanceof Date) {
    const date = moment(value)
    return date.isValid() ? date.startOf('day').toDate() : null
  }

  if (typeof value === 'string') {
    const date = moment(value, ['DD/MM/YYYY', 'YYYY-MM-DD'], true)
    return date.isValid() ? date.startOf('day').toDate() : null
  }

  if (value.$date) return parseExpiryDate(value.$date)

  return null
}

export const isNoExpiry = (item) => item?.noExpiry === true

export const getId = (value) => {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (value.$oid) return value.$oid
  return String(value)
}

const isValidNow = (item) => {
  if (isNoExpiry(item)) return true
  const expiry = parseDate(item.expiryDate)
  return !!expiry && expiry > new Date()
}

// Lets the checker search for and pick which specific asset (e.g. which
// spare defib) replaced a required slot, scoped to the same category/
// subCategory so a stretcher can't get linked as a defib's replacement.
// A slot with no subCategory is a category wildcard ("any Medical Device"),
// so only the category is sent and anything in it is offered.
// Fetches its own options so hydrating an already-'replaced' slot on load
// can resolve `value` to a real asset without the parent having to know.
const ReplacementAssetPicker = ({
  category,
  subCategory,
  value,
  onChange,
  label = 'Replacement asset',
  excludeId,
  required = true,
  helperText,
}) => {
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let active = true
    setLoading(true)
    const params = { category }
    if (String(subCategory || '').trim()) params.subCategory = subCategory
    API.get('/assets', { params })
      .then((res) => {
        // An asset can't be packed inside itself (e.g. a bag slot on a
        // bag's own checklist).
        if (active)
          setOptions(safeArray(res?.data).filter((o) => o._id !== excludeId))
      })
      .catch(() => {})
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [category, subCategory, excludeId])

  const selected = options.find((o) => o._id === value) || null

  return (
    <Autocomplete
      size="small"
      sx={{ minWidth: 260 }}
      options={options}
      loading={loading}
      value={selected}
      getOptionLabel={(o) =>
        o?.assetID
          ? `${o.assetID}${
              o.vehicle?.callSign
                ? ` — currently on ${o.vehicle.callSign}`
                : o.parentAsset?.assetID
                  ? ` — currently in ${o.parentAsset.assetID}`
                  : ''
            }`
          : ''
      }
      isOptionEqualToValue={(o, v) => o._id === v._id}
      onChange={(_, newValue) => onChange(newValue?._id || '')}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="Search asset ID…"
          error={required && !value}
          helperText={
            required && !value
              ? 'Required to submit this check'
              : helperText || ' '
          }
        />
      )}
    />
  )
}

// One expected-asset slot on a check. The checker answers a single question
// - is the recorded unit here? - with one segmented control, and only sees
// the asset picker when the answer needs a unit identifying: the slot was
// swapped for a different unit, or nothing was ever recorded for it (in
// which case identifying the unit is optional but means the next check will
// know what to expect).
const ASSET_ROW_TONES = {
  present: { border: 'success.light', bg: 'rgba(0, 150, 76, 0.05)' },
  replaced: { border: 'warning.light', bg: 'rgba(237, 108, 2, 0.05)' },
  missing: { border: 'error.light', bg: 'rgba(227, 37, 37, 0.05)' },
  removed: { border: 'error.light', bg: 'rgba(227, 37, 37, 0.05)' },
  unconfirmed: { border: 'grey.300', bg: 'grey.50' },
}

const ExpectedAssetRow = ({
  category,
  subCategory,
  slotIndex,
  slotCount,
  expected,
  state,
  isAssetCheck,
  targetId,
  targetLabel,
  onStatusChange,
  onAssign,
}) => {
  const status = state?.status || 'unconfirmed'
  // 'assigned' is just 'present' with the unit identified.
  const answer = status === 'assigned' ? 'present' : status
  const tone = ASSET_ROW_TONES[answer] || ASSET_ROW_TONES.unconfirmed
  const hasSubCategory = !!String(subCategory || '').trim()

  return (
    <Stack
      component={Paper}
      elevation={0}
      gap={1}
      sx={{
        p: 1.25,
        border: '1px solid',
        borderColor: tone.border,
        bgcolor: tone.bg,
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
        gap={1}
      >
        <Stack gap={0.25} minWidth={0}>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography fontWeight={700} noWrap>
              {hasSubCategory ? subCategory : `Any ${category}`}
            </Typography>
            {slotCount > 1 && (
              <Chip
                size="small"
                variant="outlined"
                label={`${slotIndex + 1} of ${slotCount}`}
              />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {hasSubCategory ? category : 'Any unit in this category'}
          </Typography>
        </Stack>

        {/* The unit being asked about sits with the answer buttons, so
            "is DEF-004 present?" reads as one question. */}
        <Stack gap={0.5} alignItems='stretch'>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={answer === 'unconfirmed' ? null : answer}
            onChange={(_, next) => {
              if (next) onStatusChange(next)
            }}
            sx={{
              flexShrink: 0,
              bgcolor: 'background.paper',
              '& .MuiToggleButton-root': {
                px: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                flex: { xs: 1, md: 'initial' },
              },
            }}
          >
            <ToggleButton value="present" color="success">
              <CheckRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />
              Present
            </ToggleButton>
            <ToggleButton value="missing" color="error">
              Missing
            </ToggleButton>
            <ToggleButton value="removed" color="error">
              Removed
            </ToggleButton>
            {expected && (
              <ToggleButton value="replaced" color="warning">
                Swapped
              </ToggleButton>
            )}
          </ToggleButtonGroup>
          {expected ? (
            <Card
              sx={{
                backgroundColor: 'contrast.main',
                color: 'white',
                paddingY: 0.5,
                paddingX: 1,
                alignItems: 'center'
              }}
            >
              <Typography color='white' fontSize={14} textAlign='center'>Expected Unit: {expected.assetID}</Typography>
            </Card>
          ) : (
            'No unit recorded for this slot yet'
          )}
        </Stack>
      </Stack>

      {answer === 'replaced' && (
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
          <ReplacementAssetPicker
            category={category}
            subCategory={subCategory}
            value={state?.assignedAsset}
            excludeId={isAssetCheck ? targetId : undefined}
            label={`Which unit is here instead of ${expected?.assetID}?`}
            onChange={onAssign}
          />
        </Paper>
      )}
      {answer === 'present' && !expected && (
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
          <ReplacementAssetPicker
            category={category}
            subCategory={subCategory}
            value={state?.assignedAsset}
            excludeId={isAssetCheck ? targetId : undefined}
            label="Which unit is here? (optional)"
            required={false}
            helperText={`Recording it puts the unit ${
              isAssetCheck ? 'in' : 'on'
            } ${targetLabel} so the next check knows what to expect`}
            onChange={onAssign}
          />
        </Paper>
      )}
    </Stack>
  )
}

// A required consumable/asset checklist for a single vehicle or asset. Reads
// its data from MakeReadyContext and hands the caller a submit-ready payload
// via ref instead of submitting itself, so it can be embedded either as its
// own page (Requirements.jsx) or inside a combined start-of-shift form.
const MakeReadyCheckPanel = forwardRef(
  ({ showPickListButton = true, onDirtyChange }, ref) => {
    const { loadList, inventory, setInventory, callsignAssetID, assetVehicle } =
      useContext(MakeReadyContext)
    const { orgSettings } = useContext(TenantSettingsContext)?.orgConfig || {}
    const { user } = useContext(UserContext) || {}

    const [assetStates, setAssetStates] = useState({})
    const [vehicleAssets, setVehicleAssets] = useState([])
    const [showFullInventory, setShowFullInventory] = useState(false)
    const [presentCounts, setPresentCounts] = useState({})
    const [replacementRows, setReplacementRows] = useState({})
    const [lastExpiryByItem, setLastExpiryByItem] = useState({})
    const [dirty, setDirty] = useState(false)
    const [pdfLoading, setPdfLoading] = useState(false)
    const [expandedSections, setExpandedSections] = useState({})

    useEffect(() => {
      onDirtyChange?.(dirty)
    }, [dirty])

    // ----------------------------
    // Helpers
    // ----------------------------
    const makeKey = (sectionName, name, size) =>
      `${sectionName}|${name}|${size}`
    const makeAssetKey = (sectionName, category, subCategory, index) =>
      `${sectionName}|${category}|${subCategory}|${index}`

    const normalizeQty = (q) => {
      const n = Number(q)
      if (!Number.isFinite(n) || n <= 0) return 1
      return n
    }

    // ----------------------------
    // UI Sections (supports legacy top-level requiredAssets)
    // ----------------------------
    const uiSections = useMemo(() => {
      const baseSections = safeArray(loadList?.sections).map((s) => ({
        ...s,
        requiredConsumables: safeArray(s?.requiredConsumables),
        requiredAssets: safeArray(s?.requiredAssets),
      }))

      const legacyAssets = safeArray(loadList?.requiredAssets).filter(
        (a) =>
          String(a?.category || '').trim() &&
          String(a?.subCategory || '').trim(),
      )

      if (legacyAssets.length === 0) return baseSections

      const generalIndex = baseSections.findIndex(
        (s) =>
          String(s?.sectionName || '')
            .trim()
            .toLowerCase() === 'general',
      )

      if (generalIndex === -1) {
        return [
          ...baseSections,
          {
            _id: '__general_assets__',
            sectionName: 'General',
            sectionType: '',
            requiredConsumables: [],
            requiredAssets: legacyAssets,
            __uiOnly: true,
          },
        ]
      }

      return baseSections.map((s, i) => {
        if (i !== generalIndex) return s
        return {
          ...s,
          requiredAssets: [...safeArray(s.requiredAssets), ...legacyAssets],
        }
      })
    }, [loadList])

    // ----------------------------
    // Effects
    // ----------------------------

    // Every check starts with each expected asset unconfirmed - confirming a
    // component is present is an affirmative action each time, not carried
    // over from the previous check.
    useEffect(() => {
      setAssetStates({})
    }, [inventory?._id])

    // Asset-type checklists (e.g. bags) are identified by their assetID -
    // vehicles carry a callSign instead.
    const isAssetCheck = !!assetVehicle?.assetID
    const targetLabel =
      assetVehicle?.callSign || assetVehicle?.assetID || 'this vehicle'

    // The specific asset numbers recorded on this vehicle (or inside this
    // asset, for asset-type checklists like bags), so each expected-asset
    // row can show which physical unit the checker should be seeing.
    useEffect(() => {
      setVehicleAssets([])
      if (!assetVehicle?._id) return
      let active = true
      API.get('/assets', {
        params: isAssetCheck
          ? { parentAsset: assetVehicle._id }
          : { vehicle: assetVehicle._id },
      })
        .then((res) => {
          if (active && res.status === 200)
            setVehicleAssets(safeArray(res.data))
        })
        .catch(() => {})
      return () => {
        active = false
      }
    }, [assetVehicle?._id, isAssetCheck])

    // Recorded units keyed by category|subCategory - slot N shows the Nth
    // recorded asset number of its kind.
    const expectedAssets = useMemo(() => {
      const map = {}
      vehicleAssets.forEach((asset) => {
        if (!asset.assetID) return
        const key = `${norm(asset.category)}|${norm(asset.subCategory)}`
        if (!map[key]) map[key] = []
        map[key].push({ _id: asset._id, assetID: asset.assetID })
      })
      return map
    }, [vehicleAssets])

    const validInventoryCounts = useMemo(() => {
      const next = {}

      uiSections.forEach((section) => {
        const inventoryItemsForSection =
          safeArray(inventory?.items).find(
            (inv) => inv.sectionName === section.sectionName,
          )?.items || []

        safeArray(section?.requiredConsumables).forEach((item) => {
          if (!String(item?.name || '').trim()) return
          const key = makeKey(section.sectionName, item.name, item.size)

          const validCount = inventoryItemsForSection.filter(
            (it) =>
              norm(it.name) === norm(item.name) &&
              norm(it.size) === norm(item.size) &&
              isValidNow(it),
          ).length

          next[key] = validCount
        })
      })

      return next
    }, [inventory?.items, uiSections])

    // Seed present-counts from what's actually valid (in-date) in the
    // inventory right now, so a clean bag needs zero taps to check out.
    useEffect(() => {
      setPresentCounts(validInventoryCounts)
    }, [inventory?._id, loadList?._id])

    // If a recorded in-date item is removed, immediately cap the UI count to
    // the stock still present. This preserves deliberate shortage edits while
    // keeping the row totals honest after removals.
    useEffect(() => {
      setPresentCounts((prev) => {
        const next = { ...prev }
        let changed = false

        Object.entries(next).forEach(([key, value]) => {
          const maxRecorded = validInventoryCounts[key] ?? 0
          if (value > maxRecorded) {
            next[key] = maxRecorded
            changed = true
          }
        })

        Object.keys(validInventoryCounts).forEach((key) => {
          if (next[key] === undefined) {
            next[key] = validInventoryCounts[key]
            changed = true
          }
        })

        return changed ? next : prev
      })
    }, [validInventoryCounts])

    // Keep a replacement-booking row available for every unit still needed.
    useEffect(() => {
      setReplacementRows((prev) => {
        const next = { ...prev }
        const validKeys = new Set()

        uiSections.forEach((section) => {
          safeArray(section?.requiredConsumables)
            .filter((item) => String(item?.name || '').trim())
            .forEach((item) => {
              const key = makeKey(section.sectionName, item.name, item.size)
              const requiredQty = normalizeQty(item.quantity)
              const presentQty = presentCounts[key] ?? 0
              const needed = Math.max(0, requiredQty - presentQty)
              validKeys.add(key)

              const current = safeArray(next[key])
              if (needed > current.length) {
                next[key] = [
                  ...current,
                  ...Array.from(
                    { length: needed - current.length },
                    (_, i) => ({
                      id: `${key}-${Date.now()}-${i}`,
                      expiryDate: '',
                      touched: false,
                    }),
                  ),
                ]
              } else if (needed < current.length) {
                next[key] = current.slice(0, needed)
              }
            })
        })

        Object.keys(next).forEach((key) => {
          if (!validKeys.has(key)) delete next[key]
        })

        return next
      })
    }, [presentCounts, uiSections])

    // ----------------------------
    // Handlers - Consumables
    // ----------------------------
    const setPresentCount = (key, count) => {
      const maxRecorded = validInventoryCounts[key] ?? 0
      setDirty(true)
      setPresentCounts((prev) => ({
        ...prev,
        [key]: Math.max(0, Math.min(maxRecorded, Number(count) || 0)),
      }))
    }

    const handleRemove = (itemToRemove, sectionName) => {
      setDirty(true)
      setInventory((prev) => {
        const updatedItems = safeArray(prev?.items).map((section) => {
          if (section.sectionName !== sectionName) return section

          const existing = safeArray(section.items)

          if (itemToRemove?._id || itemToRemove?.id) {
            const targetId = getId(itemToRemove._id || itemToRemove.id)
            let removed = false

            const filtered = existing.filter((it) => {
              const currentId = getId(it._id || it.id)
              if (!removed && currentId === targetId) {
                removed = true
                return false
              }
              return true
            })

            return { ...section, items: filtered }
          }

          let removed = false
          const filtered = existing.filter((it) => {
            if (
              !removed &&
              it.name === itemToRemove.name &&
              it.size === itemToRemove.size
            ) {
              removed = true
              return false
            }
            return true
          })

          return { ...section, items: filtered }
        })

        return { ...prev, items: updatedItems }
      })
    }

    // "Remove everything": wipes every recorded item in every section so a
    // bag that has been almost fully used can be recorded from scratch,
    // instead of deleting stale items one at a time. Local only - like
    // single-item removal, nothing reaches the server until the check is
    // completed. Section shells are kept so the load list still lines up.
    const [confirmClearOpen, setConfirmClearOpen] = useState(false)
    const recordedItemCount = safeArray(inventory?.items).reduce(
      (total, section) => total + safeArray(section?.items).length,
      0,
    )

    const handleClearAll = () => {
      setConfirmClearOpen(false)
      if (recordedItemCount === 0) return
      setDirty(true)
      setInventory((prev) => ({
        ...prev,
        items: safeArray(prev?.items).map((section) => ({
          ...section,
          items: [],
        })),
      }))
      // Present counts are capped to recorded stock by the effect above, but
      // reset explicitly so the rows read zero immediately.
      setPresentCounts({})
    }

    // Clears every recorded item in one inventory section - used for
    // sections the load list no longer has (renamed or removed), whose stock
    // would otherwise never be shown or removable here.
    const handleRemoveSection = (sectionName) => {
      setDirty(true)
      setInventory((prev) => ({
        ...prev,
        items: safeArray(prev?.items).map((section) =>
          section.sectionName === sectionName
            ? { ...section, items: [] }
            : section,
        ),
      }))
    }

    const handleReplacementDateChange = (key, index, value) => {
      const expiryDate = parseExpiryDate(value)
      if (expiryDate) {
        setLastExpiryByItem((prev) => ({ ...prev, [key]: expiryDate }))
      }

      setReplacementRows((prev) => {
        const rows = [...safeArray(prev[key])]
        rows[index] = { ...rows[index], expiryDate: value, touched: true }
        return { ...prev, [key]: rows }
      })
    }

    const addInventoryItem = (sectionName, reqItem, entry) => {
      const expiryDate = parseExpiryDate(entry?.expiryDate)

      if (!reqItem.noExpiry && !expiryDate) return false

      const newItem = {
        id: entry?.id || `${sectionName}-${reqItem.name}-${Date.now()}`,
        name: reqItem.name,
        size: reqItem.size,
        expiryDate: reqItem.noExpiry ? null : expiryDate,
        noExpiry: !!reqItem.noExpiry,
        addedAt: new Date(),
        addedBy: null,
      }

      setInventory((prev) => {
        const safePrev = prev || { items: [] }
        const itemsArray = safeArray(safePrev.items)
        const sectionIndex = itemsArray.findIndex(
          (s) => s.sectionName === sectionName,
        )

        const updatedItems = [...itemsArray]
        if (sectionIndex !== -1) {
          updatedItems[sectionIndex] = {
            ...updatedItems[sectionIndex],
            items: [...safeArray(updatedItems[sectionIndex].items), newItem],
          }
        } else {
          updatedItems.push({ sectionName, items: [newItem] })
        }

        return { ...safePrev, items: updatedItems }
      })

      return true
    }

    const handleAddReplacement = (
      sectionName,
      reqItem,
      index,
      overrideExpiryDate,
    ) => {
      const key = makeKey(sectionName, reqItem.name, reqItem.size)
      const currentEntry = safeArray(replacementRows[key])[index]
      const entry = overrideExpiryDate
        ? { ...currentEntry, expiryDate: overrideExpiryDate }
        : currentEntry

      if (!addInventoryItem(sectionName, reqItem, entry)) return

      setDirty(true)
      const expiryDate = parseExpiryDate(entry?.expiryDate)
      if (expiryDate) {
        setLastExpiryByItem((prev) => ({ ...prev, [key]: expiryDate }))
      }
      setReplacementRows((prev) => {
        const rows = safeArray(prev[key]).filter((_, i) => i !== index)
        return { ...prev, [key]: rows }
      })
      setPresentCounts((prev) => ({
        ...prev,
        [key]: Math.max(0, (prev[key] ?? 0) + 1),
      }))
    }

    // Only ever truncates the valid (in-date) matching items down to the
    // confirmed present count - expired/obsolete items are left untouched
    // here and only removed via their own explicit Remove buttons.
    const applyPresentCountsToInventory = (inv) => {
      const sectionMap = new Map()
      uiSections.forEach((section) => {
        sectionMap.set(
          section.sectionName,
          safeArray(section?.requiredConsumables).filter((item) =>
            String(item?.name || '').trim(),
          ),
        )
      })

      const updatedSections = safeArray(inv?.items).map((section) => {
        const requiredItems = sectionMap.get(section.sectionName) || []
        let sectionItems = safeArray(section.items)

        requiredItems.forEach((reqItem) => {
          const key = makeKey(section.sectionName, reqItem.name, reqItem.size)
          const targetCount = presentCounts[key]

          if (targetCount === undefined) return

          const matchesReq = (item) =>
            norm(item.name) === norm(reqItem.name) &&
            norm(item.size) === norm(reqItem.size)

          const validMatching = sectionItems.filter(
            (item) => matchesReq(item) && isValidNow(item),
          )
          const otherItems = sectionItems.filter(
            (item) => !(matchesReq(item) && isValidNow(item)),
          )

          sectionItems = [...otherItems, ...validMatching.slice(-targetCount)]
        })

        return { ...section, items: sectionItems }
      })

      return { ...inv, items: updatedSections }
    }

    // ----------------------------
    // Handlers - Assets
    // ----------------------------
    const setAssetStatus = (assetKey, status) => {
      setDirty(true)
      setAssetStates((prev) => {
        const cur = prev[assetKey] || {
          status: 'unconfirmed',
          assignedAsset: '',
        }
        // Clear out a stale asset link if the status changes away from
        // 'replaced'/'assigned', so it can't silently carry over to a
        // different status later.
        return {
          ...prev,
          [assetKey]: {
            ...cur,
            status,
            assignedAsset: ['replaced', 'assigned'].includes(status)
              ? cur.assignedAsset
              : '',
          },
        }
      })
    }

    const setAssetAssignment = (assetKey, assignedAsset) => {
      setDirty(true)
      setAssetStates((prev) => {
        const cur = prev[assetKey] || { status: 'replaced', assignedAsset: '' }
        // A 'present' slot with a unit picked is what the server knows as
        // 'assigned' (unit is here, and now we've recorded which); clearing
        // the pick drops it back to plain 'present'.
        const status =
          cur.status === 'replaced'
            ? 'replaced'
            : assignedAsset
              ? 'assigned'
              : 'present'
        return { ...prev, [assetKey]: { ...cur, status, assignedAsset } }
      })
    }

    // One tap to confirm every still-unchecked slot in a section as present
    // - still an affirmative action, just for the common "everything's
    // where it should be" case.
    const markSectionAssetsPresent = (section) => {
      setDirty(true)
      setAssetStates((prev) => {
        const next = { ...prev }
        safeArray(section?.requiredAssets)
          .filter(
            (a) =>
              String(a?.category || '').trim() &&
              String(a?.subCategory || '').trim(),
          )
          .forEach((a) => {
            const qty = normalizeQty(a.quantity)
            for (let idx = 0; idx < qty; idx++) {
              const key = makeAssetKey(
                section.sectionName,
                a.category,
                a.subCategory,
                idx,
              )
              if (!next[key] || next[key].status === 'unconfirmed') {
                next[key] = { status: 'present', assignedAsset: '' }
              }
            }
          })
        return next
      })
    }

    const getAssetCountsForSection = (section) => {
      const requiredAssets = safeArray(section?.requiredAssets).filter(
        (a) =>
          String(a?.category || '').trim() &&
          String(a?.subCategory || '').trim(),
      )

      if (requiredAssets.length === 0) return { issues: 0, unconfirmed: 0 }

      let issues = 0
      let unconfirmed = 0

      requiredAssets.forEach((a) => {
        const qty = normalizeQty(a.quantity)
        for (let idx = 0; idx < qty; idx++) {
          const key = makeAssetKey(
            section.sectionName,
            a.category,
            a.subCategory,
            idx,
          )

          const status = assetStates?.[key]?.status || 'unconfirmed'
          if (status === 'unconfirmed') unconfirmed += 1
          else if (status !== 'present' && status !== 'assigned') issues += 1
        }
      })

      return { issues, unconfirmed }
    }

    // Every expected-asset slot this load list defines - used to validate
    // that each one has been affirmatively confirmed before submit.
    const getAllRequiredAssetKeys = () =>
      uiSections.flatMap((section) =>
        safeArray(section?.requiredAssets)
          .filter(
            (a) =>
              String(a?.category || '').trim() &&
              String(a?.subCategory || '').trim(),
          )
          .flatMap((asset) => {
            const qty = normalizeQty(asset.quantity)
            return Array.from({ length: qty }, (_, index) =>
              makeAssetKey(
                section.sectionName,
                asset.category,
                asset.subCategory,
                index,
              ),
            )
          }),
      )

    // ----------------------------
    // Payload builder (exposed to parent via ref)
    // ----------------------------
    const buildPayload = () => {
      const inventoryForSubmit = applyPresentCountsToInventory(inventory)

      const assetConfirmations = uiSections.flatMap((section) =>
        safeArray(section?.requiredAssets)
          .filter(
            (a) =>
              String(a?.category || '').trim() &&
              String(a?.subCategory || '').trim(),
          )
          .flatMap((asset) => {
            const qty = normalizeQty(asset.quantity)
            return Array.from({ length: qty }, (_, index) => {
              const key = makeAssetKey(
                section.sectionName,
                asset.category,
                asset.subCategory,
                index,
              )
              const state = assetStates[key] || { status: 'unconfirmed' }
              return {
                sectionName: section.sectionName,
                category: asset.category,
                subCategory: asset.subCategory,
                index,
                status: state.status || 'unconfirmed',
                assignedAsset: state.assignedAsset || null,
                // Not stored on the inventory - lets the server take the
                // swapped-out unit off this vehicle/asset when a slot is
                // marked 'replaced'.
                replacedAsset:
                  state.status === 'replaced'
                    ? expectedAssets[
                        `${norm(asset.category)}|${norm(asset.subCategory)}`
                      ]?.[index]?._id || null
                    : null,
                notes: state.notes || '',
                checkedAt: new Date(),
                checkedBy: null,
              }
            })
          }),
      )

      const legacyOrphanAssets = Object.entries(assetStates)
        .filter(([key]) => {
          return !assetConfirmations.some((asset) => {
            const currentKey = makeAssetKey(
              asset.sectionName,
              asset.category,
              asset.subCategory,
              asset.index,
            )
            return currentKey === key
          })
        })
        .map(([key, val]) => {
          const [sectionName, category, subCategory, index] = key.split('|')
          return {
            sectionName,
            category,
            subCategory,
            index: Number(index),
            status: val.status || 'present',
            assignedAsset: val.assignedAsset || null,
            notes: val.notes || '',
            checkedAt: new Date(),
            checkedBy: null,
          }
        })

      return {
        ...inventoryForSubmit,
        callsignAssetID,
        assets: [...assetConfirmations, ...legacyOrphanAssets],
      }
    }

    useImperativeHandle(ref, () => ({
      getPayload: buildPayload,
      isDirty: () => dirty,
      clearDirty: () => setDirty(false),
      // False if any expected asset hasn't been affirmatively confirmed, or
      // is marked 'Replaced' without picking which asset actually replaced
      // it - otherwise that link stays null forever.
      isValid: () =>
        getAllRequiredAssetKeys().every((key) => {
          const state = assetStates[key]
          if (!state || state.status === 'unconfirmed') return false
          if (['replaced', 'assigned'].includes(state.status)) {
            return !!state.assignedAsset
          }
          return true
        }),
      getIssueSummary: () => ({
        missingItems: neededItems.reduce(
          (total, item) => total + (Number(item.missing) || 0),
          0,
        ),
        expiredItems: allExpiredItems.length,
        assetIssues: allAssetIssues.length,
        totalIssues:
          neededItems.reduce(
            (total, item) => total + (Number(item.missing) || 0),
            0,
          ) +
          allExpiredItems.length +
          allAssetIssues.length,
      }),
    }))

    // ----------------------------
    // Collect Pick List Items / Summary
    // ----------------------------
    const allExpiredItems = []
    const allExpiringSoonItems = []
    const allAssetIssues = []
    const neededItems = []
    const sectionSummaries = []

    ;(uiSections ?? []).forEach((section) => {
      const inventoryItemsForSection =
        safeArray(inventory?.items).find(
          (inv) => inv.sectionName === section.sectionName,
        )?.items || []

      const {
        expired,
        almostExpired,
        expiredItems,
        expiringSoonItems,
        obsoleteItems,
      } = getSectionStatusSummary(section, inventoryItemsForSection, [])
      const { issues: assetIssues, unconfirmed: assetsUnconfirmed } =
        getAssetCountsForSection(section)

      let sectionNeeded = 0
      safeArray(section?.requiredConsumables).forEach((item) => {
        if (!String(item?.name || '').trim()) return
        const key = makeKey(section.sectionName, item.name, item.size)
        const requiredQty = normalizeQty(item.quantity)
        const presentQty = presentCounts[key] ?? 0
        const needed = Math.max(0, requiredQty - presentQty)

        if (needed > 0) {
          sectionNeeded += needed
          neededItems.push({
            section: section.sectionName,
            name: item.name,
            size: item.size,
            missing: needed,
            noExpiry: item.noExpiry ?? false,
          })
        }
      })

      safeArray(section?.requiredAssets)
        .filter(
          (a) =>
            String(a?.category || '').trim() &&
            String(a?.subCategory || '').trim(),
        )
        .forEach((asset) => {
          const qty = normalizeQty(asset.quantity)
          for (let index = 0; index < qty; index++) {
            const assetKey = makeAssetKey(
              section.sectionName,
              asset.category,
              asset.subCategory,
              index,
            )
            const status = assetStates?.[assetKey]?.status || 'unconfirmed'
            if (!['present', 'unconfirmed', 'assigned'].includes(status)) {
              allAssetIssues.push({
                section: section.sectionName,
                name: `${asset.category} - ${asset.subCategory}`,
                size: status,
                missing: 1,
              })
            }
          }
        })

      sectionSummaries.push({
        sectionName: section.sectionName,
        needed: sectionNeeded,
        expired,
        almostExpired,
        obsolete: obsoleteItems.length,
        assetIssues,
        assetsUnconfirmed,
      })

      allExpiredItems.push(
        ...expiredItems.map((i) => ({ ...i, section: section.sectionName })),
      )
      allExpiringSoonItems.push(
        ...expiringSoonItems.map((i) => ({
          ...i,
          section: section.sectionName,
        })),
      )
    })

    const totals = sectionSummaries.reduce(
      (acc, section) => ({
        needed: acc.needed + section.needed,
        expired: acc.expired + section.expired,
        almostExpired: acc.almostExpired + section.almostExpired,
        obsolete: acc.obsolete + section.obsolete,
        assetIssues: acc.assetIssues + section.assetIssues,
        assetsUnconfirmed: acc.assetsUnconfirmed + section.assetsUnconfirmed,
      }),
      {
        needed: 0,
        expired: 0,
        almostExpired: 0,
        obsolete: 0,
        assetIssues: 0,
        assetsUnconfirmed: 0,
      },
    )

    // Inventory sections with stock that no section on the current load list
    // matches (renamed/removed section, or a different list assigned). The
    // board ignores this stock for expiry (server helpers/makeReady/
    // loadListItems.js) but it's still saved on every check until removed.
    const orphanSections = safeArray(inventory?.items).filter(
      (inv) =>
        safeArray(inv?.items).length > 0 &&
        !(uiSections ?? []).some((s) => s.sectionName === inv.sectionName),
    )
    const orphanItemCount = orphanSections.reduce(
      (acc, inv) => acc + inv.items.length,
      0,
    )

    const needsReview = totals.almostExpired + totals.obsolete + orphanItemCount
    const ready =
      totals.needed === 0 &&
      totals.expired === 0 &&
      needsReview === 0 &&
      totals.assetIssues === 0 &&
      totals.assetsUnconfirmed === 0

    const SummaryCard = ({ label, value, tone, helper }) => {
      const colorMap = {
        ready: 'anGreen.main',
        restock: 'anRed.main',
        review: 'anOrange.main',
        neutral: 'primary.main',
      }

      return (
        <Stack
          component={Paper}
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'grey.300',
            borderLeft: '6px solid',
            borderLeftColor: colorMap[tone] || 'primary.main',
            p: 1.25,
            minHeight: 88,
            borderRadius: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="title2" fontSize="1.4rem">
            {value}
          </Typography>
          {helper && (
            <Typography variant="caption" color="text.secondary">
              {helper}
            </Typography>
          )}
        </Stack>
      )
    }

    // ----------------------------
    // Render
    // ----------------------------
    return (
      <Stack gap={2}>
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: '1fr 1fr',
            lg: 'repeat(4, minmax(0, 1fr))',
          }}
          gap={1}
        >
          <SummaryCard
            label="Ready"
            value={ready ? 'Yes' : 'No'}
            tone={ready ? 'ready' : 'neutral'}
            helper={ready ? 'All checks clear' : 'Action needed'}
          />
          <SummaryCard
            label="Needs restock"
            value={totals.needed + totals.expired}
            tone={totals.needed + totals.expired ? 'restock' : 'neutral'}
            helper="Missing or expired"
          />
          <SummaryCard
            label="Needs review"
            value={needsReview}
            tone={needsReview ? 'review' : 'neutral'}
            helper="Expiring soon or obsolete"
          />
          <SummaryCard
            label="Asset issues"
            value={totals.assetIssues}
            tone={totals.assetIssues ? 'restock' : 'neutral'}
            helper={
              totals.assetsUnconfirmed
                ? `${totals.assetsUnconfirmed} still to confirm`
                : 'Missing, removed or replaced'
            }
          />
        </Box>

        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          gap={1}
          alignItems={{ xs: 'stretch', lg: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" gap={1} alignItems="center">
            <Switch
              checked={showFullInventory}
              onChange={(e) => setShowFullInventory(e.target.checked)}
            />
            <Typography>Show recorded stock</Typography>
          </Stack>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            gap={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Button
              variant="outlined"
              color="error"
              size="small"
              disabled={recordedItemCount === 0}
              onClick={() => setConfirmClearOpen(true)}
              startIcon={<DeleteSweepOutlinedIcon />}
            >
              Remove everything
            </Button>
            {showPickListButton && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={pdfLoading}
                onClick={async () => {
                  setPdfLoading(true)
                  try {
                    await downloadPdf(
                      <PickList
                        expiredItems={allExpiredItems}
                        expiringSoonItems={allExpiringSoonItems}
                        missingItems={neededItems}
                        assetIssues={allAssetIssues}
                        assetVehicle={assetVehicle}
                        loadListTitle={loadList?.title}
                        orgSettings={orgSettings}
                        generatedBy={[user?.firstName, user?.lastName]
                          .filter(Boolean)
                          .join(' ')}
                      />,
                      `pick-list-${String(
                        assetVehicle?.callSign ?? assetVehicle?.assetID ?? 'make-ready',
                      )
                        .replace(/[^A-Za-z0-9-]+/g, '-')
                        .toLowerCase()}-${moment().format('YYYY-MM-DD')}.pdf`,
                    )
                  } catch (e) {
                    console.error(e)
                  } finally {
                    setPdfLoading(false)
                  }
                }}
                startIcon={<FileDownloadOutlinedIcon />}
              >
                {pdfLoading ? 'Preparing PDF...' : 'Download Pick List'}
              </Button>
            )}
          </Stack>
        </Stack>

        <Dialog
          open={confirmClearOpen}
          onClose={() => setConfirmClearOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Remove all recorded stock?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This clears {recordedItemCount} recorded{' '}
              {recordedItemCount === 1 ? 'item' : 'items'} across every section
              of{' '}
              <strong>
                {assetVehicle?.callSign ??
                  assetVehicle?.assetID ??
                  'this check'}
              </strong>{' '}
              so you can record it from scratch. Every required item will show
              as missing until you add stock back. Nothing is saved until you
              complete the check.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmClearOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleClearAll}
              startIcon={<DeleteSweepOutlinedIcon />}
            >
              Remove everything
            </Button>
          </DialogActions>
        </Dialog>

        <Alert severity="info" variant="outlined" sx={{ borderRadius: 1 }}>
          Recorded counts are pre-filled from in-date stock already on file.
          Reduce the count if something is not physically present. Any shortfall
          will be saved as a missing item for restock and KPI monitoring.
        </Alert>

        {(uiSections ?? []).map((section) => {
          const inventoryItemsForSection =
            safeArray(inventory?.items).find(
              (inv) => inv.sectionName === section.sectionName,
            )?.items || []

          const {
            expired,
            almostExpired,
            expiredItems,
            expiringSoonItems,
            inDateItems,
            obsoleteItems,
          } = getSectionStatusSummary(section, inventoryItemsForSection, [])

          const { issues: assetIssues, unconfirmed: assetsUnconfirmed } =
            getAssetCountsForSection(section)

          const requiredAssets = safeArray(section?.requiredAssets).filter(
            (a) =>
              String(a?.category || '').trim() &&
              String(a?.subCategory || '').trim(),
          )

          const requiredConsumables = safeArray(section?.requiredConsumables)
            .filter((item) => String(item?.name || '').trim())
            .map((item) => {
              const key = makeKey(section.sectionName, item.name, item.size)
              const requiredQty = normalizeQty(item.quantity)
              const presentQty = presentCounts[key] ?? 0
              return {
                ...item,
                key,
                requiredQty,
                presentQty,
                recordedQty: validInventoryCounts[key] ?? 0,
                needed: Math.max(0, requiredQty - presentQty),
              }
            })

          const getMatchesRequiredConsumable = (reqItem) => (item) =>
            norm(item.name) === norm(reqItem.name) &&
            norm(item.size) === norm(reqItem.size)

          const sectionNeeded = requiredConsumables.reduce(
            (acc, item) => acc + item.needed,
            0,
          )

          const totalIssues =
            sectionNeeded +
            expired +
            almostExpired +
            obsoleteItems.length +
            assetIssues +
            assetsUnconfirmed
          const sectionKey = section._id || section.sectionName

          return (
            <Accordion
              key={sectionKey}
              expanded={expandedSections[sectionKey] ?? totalIssues > 0}
              onChange={(_, expanded) =>
                setExpandedSections((prev) => ({
                  ...prev,
                  [sectionKey]: expanded,
                }))
              }
              disableGutters
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: '#fff' }} />}
                sx={{
                  backgroundColor:
                    sectionNeeded > 0 || expired > 0 || assetIssues > 0
                      ? 'anRed.main'
                      : almostExpired > 0
                        ? 'anOrange.main'
                        : obsoleteItems.length > 0
                          ? 'contrast.main'
                          : assetsUnconfirmed > 0
                            ? 'anMainBlue.main'
                            : 'anGreen.main',
                  color: '#fff',
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    minWidth: 0,
                  },
                }}
              >
                <Stack
                  direction={{ xs: 'column', lg: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', lg: 'center' }}
                  width="100%"
                  gap={1}
                >
                  <Stack gap={0.25}>
                    <Typography color="inherit" fontWeight={700}>
                      {section.sectionName || 'Unnamed section'}
                    </Typography>
                    {totalIssues === 0 && (
                      <Typography color="inherit" variant="caption">
                        No action needed
                      </Typography>
                    )}
                  </Stack>
                  <Stack direction="row" gap={0.75} flexWrap="wrap">
                    {sectionNeeded > 0 && (
                      <Chip
                        size="small"
                        label={`${sectionNeeded} needed`}
                        sx={{ bgcolor: '#fff', color: 'anRed.main' }}
                      />
                    )}
                    {assetIssues > 0 && (
                      <Chip
                        size="small"
                        label={`${assetIssues} asset issue`}
                        sx={{ bgcolor: '#fff', color: 'anRed.main' }}
                      />
                    )}
                    {assetsUnconfirmed > 0 && (
                      <Chip
                        size="small"
                        label={`${assetsUnconfirmed} to confirm`}
                        sx={{ bgcolor: '#fff', color: 'anMainBlue.main' }}
                      />
                    )}
                    {expired > 0 && (
                      <Chip
                        size="small"
                        label={`${expired} expired`}
                        sx={{ bgcolor: '#fff', color: 'anRed.main' }}
                      />
                    )}
                    {almostExpired > 0 && (
                      <Chip
                        size="small"
                        label={`${almostExpired} expiring soon`}
                        sx={{ bgcolor: '#fff', color: 'anOrange.main' }}
                      />
                    )}
                    {obsoleteItems.length > 0 && (
                      <Chip
                        size="small"
                        label={`${obsoleteItems.length} obsolete`}
                        sx={{ bgcolor: '#fff', color: 'contrast.main' }}
                      />
                    )}
                    {totalIssues === 0 && (
                      <Chip
                        size="small"
                        label="Clear"
                        sx={{ bgcolor: '#fff', color: 'anGreen.main' }}
                      />
                    )}
                  </Stack>
                </Stack>
              </AccordionSummary>

              <AccordionDetails sx={{ bgcolor: 'background.paper', p: 1.5 }}>
                <Stack
                  gap={1}
                  divider={
                    <Divider
                      sx={{
                        borderColor: 'divider',
                      }}
                    />
                  }
                >
                  {requiredConsumables.map((item) => {
                    const matchesItem = getMatchesRequiredConsumable(item)
                    const itemExpiredRows = expiredItems.filter(matchesItem)
                    const itemExpiringSoonRows =
                      expiringSoonItems.filter(matchesItem)
                    const itemRecordedRows = inDateItems.filter(matchesItem)

                    return (
                      <Stack key={item.key} gap={0.5}>
                        <RequiredItemRow
                          item={item}
                          replacementRows={safeArray(replacementRows[item.key])}
                          lastExpiryDate={lastExpiryByItem[item.key]}
                          onReplacementDateChange={(index, value) =>
                            handleReplacementDateChange(item.key, index, value)
                          }
                          onAddReplacement={(index) =>
                            handleAddReplacement(
                              section.sectionName,
                              item,
                              index,
                            )
                          }
                          onUseLastExpiry={(index, expiryDate) =>
                            handleAddReplacement(
                              section.sectionName,
                              item,
                              index,
                              expiryDate,
                            )
                          }
                          expiredRows={itemExpiredRows}
                          expiringSoonRows={itemExpiringSoonRows}
                          recordedRows={itemRecordedRows}
                          showRecordedStock={showFullInventory}
                          onRemoveExpired={(expiredItem) =>
                            handleRemove(expiredItem, section.sectionName)
                          }
                          onRemoveExpiringSoon={(soonItem) =>
                            handleRemove(soonItem, section.sectionName)
                          }
                          onRemoveRecorded={(recordedItem) =>
                            handleRemove(recordedItem, section.sectionName)
                          }
                        />
                      </Stack>
                    )
                  })}

                  {/* Obsolete */}
                  {obsoleteItems.map((item, i) => (
                    <ObsoleteRow
                      key={`obsolete-${section.sectionName}-${i}`}
                      item={item}
                      onRemove={() => handleRemove(item, section.sectionName)}
                    />
                  ))}

                  {/* Section Assets */}
                  {requiredAssets.length > 0 && (
                    <>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={1}
                        flexWrap="wrap"
                      >
                        <Stack>
                          <Typography variant="subtitle2" fontWeight={700}>
                            Expected assets
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Confirm each unit is {isAssetCheck ? 'in' : 'on'}{' '}
                            {targetLabel}
                          </Typography>
                        </Stack>
                        {assetsUnconfirmed > 0 && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            startIcon={<CheckRoundedIcon />}
                            onClick={() => markSectionAssetsPresent(section)}
                          >
                            All {assetsUnconfirmed} present
                          </Button>
                        )}
                      </Stack>

                      <Stack spacing={1} sx={{ mt: 1 }}>
                        {requiredAssets.flatMap((asset) => {
                          const qty = normalizeQty(asset.quantity)
                          return Array.from({ length: qty }, (_, idx) => {
                            const assetKey = makeAssetKey(
                              section.sectionName,
                              asset.category,
                              asset.subCategory,
                              idx,
                            )
                            return (
                              <ExpectedAssetRow
                                key={assetKey}
                                category={asset.category}
                                subCategory={asset.subCategory}
                                slotIndex={idx}
                                slotCount={qty}
                                expected={
                                  expectedAssets[
                                    `${norm(asset.category)}|${norm(asset.subCategory)}`
                                  ]?.[idx] || null
                                }
                                state={assetStates[assetKey]}
                                isAssetCheck={isAssetCheck}
                                targetId={assetVehicle?._id}
                                targetLabel={targetLabel}
                                onStatusChange={(status) =>
                                  setAssetStatus(assetKey, status)
                                }
                                onAssign={(id) =>
                                  setAssetAssignment(assetKey, id)
                                }
                              />
                            )
                          })
                        })}
                      </Stack>
                    </>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )
        })}

        {orphanSections.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              borderLeft: '6px solid',
              borderLeftColor: 'anOrange.main',
              borderRadius: 1,
            }}
          >
            <Stack gap={1}>
              <Stack>
                <Typography fontWeight={700}>
                  No longer on this load list ({orphanItemCount})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Stock recorded under sections this load list no longer has.
                  It doesn&apos;t count towards expiry - remove anything that
                  isn&apos;t {isAssetCheck ? 'in' : 'on'} {targetLabel}.
                </Typography>
              </Stack>
              {orphanSections.map((inv) => (
                <Stack key={`orphan-${inv.sectionName}`} gap={1}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1}
                    flexWrap="wrap"
                  >
                    <Typography variant="subtitle2" fontWeight={700}>
                      {inv.sectionName || 'Unnamed section'}
                    </Typography>
                    <Button
                      size="small"
                      color="inherit"
                      startIcon={<DeleteSweepOutlinedIcon />}
                      onClick={() => handleRemoveSection(inv.sectionName)}
                    >
                      Remove all {inv.items.length}
                    </Button>
                  </Stack>
                  {inv.items.map((item, i) => (
                    <ObsoleteRow
                      key={`orphan-${inv.sectionName}-${item._id ?? i}`}
                      item={item}
                      onRemove={() => handleRemove(item, inv.sectionName)}
                    />
                  ))}
                </Stack>
              ))}
            </Stack>
          </Paper>
        )}
      </Stack>
    )
  },
)

MakeReadyCheckPanel.displayName = 'MakeReadyCheckPanel'

export default MakeReadyCheckPanel
